// @oasis-permission-inherits: /ai-edit-tool (ai-edit-v2-tool:view)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { PermissionBanner } from "~/components/permission-banner";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { ScrollArea } from "~/components/ui/scroll-area";
import { CaptionColorOverridePicker } from "~/modules/ai-edit-tool/components/caption-color-override-picker";
import { useStyleEditorPermissions } from "~/modules/ai-edit-tool/hooks/use-style-editor-permissions";
import {
  ColorPresetDraft,
  emptyColorPresetDraft,
  parseRgbChannel,
} from "~/modules/ai-edit-tool/lib/color-preset-draft";
import { createAiEditStyle } from "~/modules/ai-edit-tool/network/mutations";
import { getCaptionsTemplates } from "~/modules/ai-edit-tool/network/queries";
import {
  CaptionsColorOverrideConfig,
  Color,
  type AspectRatio,
} from "~/modules/ai-edit-tool/schemas";
import {
  hexToRgbColorChannels,
  rgbColorChannelsToHex,
} from "~/modules/ai-edit-tool/utils/color-conversion";

export const Route = createFileRoute("/_dash/ai-edit-tool/new/vst")({
  component: RouteComponent,
});

function RouteComponent() {
  const { canEdit } = useStyleEditorPermissions();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState("");
  const [captionTemplate, setCaptionTemplate] = useState("");
  const [colorPresets, setColorPresets] = useState<Color[]>([]);
  const [captionsColorOverrides, setCaptionsColorOverrides] = useState<
    CaptionsColorOverrideConfig[]
  >([]);
  const [supportedAspectRatios, setSupportedAspectRatios] = useState<AspectRatio[]>(["9:16"]);
  const [captionTemplatesOpen, setCaptionTemplatesOpen] = useState(false);
  const [captionTemplatesSearch, setCaptionTemplatesSearch] = useState("");
  const [colorPresetDraft, setColorPresetDraft] = useState<ColorPresetDraft>(emptyColorPresetDraft);
  const [colorPresetHexError, setColorPresetHexError] = useState("");

  const { data: captionsTemplatesData, isLoading: captionsTemplatesLoading } =
    getCaptionsTemplates.useQuery({
      params: {},
    });

  const captionTemplates = useMemo(
    () => captionsTemplatesData?.templates ?? [],
    [captionsTemplatesData?.templates]
  );
  const selectedCaptionTemplate = captionTemplates.find(
    (template) => template.id === captionTemplate
  );
  const filteredCaptionTemplates = useMemo(() => {
    const query = captionTemplatesSearch.trim().toLowerCase();
    if (!query) {
      return captionTemplates;
    }
    return captionTemplates.filter((template) => {
      const name = template.displayName?.toLowerCase() ?? "";
      return name.includes(query) || template.id.toLowerCase().includes(query);
    });
  }, [captionTemplates, captionTemplatesSearch]);
  const parsedColorPresetDraft = useMemo(() => {
    const red = parseRgbChannel(colorPresetDraft.red);
    const green = parseRgbChannel(colorPresetDraft.green);
    const blue = parseRgbChannel(colorPresetDraft.blue);

    if (red === null || green === null || blue === null) {
      return null;
    }

    return { red, green, blue };
  }, [colorPresetDraft.blue, colorPresetDraft.green, colorPresetDraft.red]);
  const colorPresetPreview = parsedColorPresetDraft
    ? `rgb(${parsedColorPresetDraft.red}, ${parsedColorPresetDraft.green}, ${parsedColorPresetDraft.blue})`
    : undefined;

  const setColorPresetDraftFromHex = useCallback((hexValue: string, showError: boolean) => {
    const parsed = hexToRgbColorChannels(hexValue);
    if (!parsed) {
      if (showError) {
        setColorPresetHexError("Use #RGB or #RRGGBB");
      }
      return false;
    }

    setColorPresetDraft({
      red: parsed.red.toString(),
      green: parsed.green.toString(),
      blue: parsed.blue.toString(),
      hex: rgbColorChannelsToHex(parsed),
    });
    setColorPresetHexError("");
    return true;
  }, []);

  const onColorPresetChannelChange = useCallback(
    (channel: "red" | "green" | "blue", value: string) => {
      setColorPresetDraft((previous) => {
        const next = { ...previous, [channel]: value };
        const red = parseRgbChannel(next.red);
        const green = parseRgbChannel(next.green);
        const blue = parseRgbChannel(next.blue);

        if (red === null || green === null || blue === null) {
          return next;
        }

        return {
          ...next,
          hex: rgbColorChannelsToHex({ red, green, blue }),
        };
      });
      setColorPresetHexError("");
    },
    []
  );

  const onAddColorPreset = useCallback(() => {
    if (!parsedColorPresetDraft) {
      return;
    }

    setColorPresets((previous) => [
      ...previous,
      {
        red: parsedColorPresetDraft.red,
        green: parsedColorPresetDraft.green,
        blue: parsedColorPresetDraft.blue,
        alpha: 1,
      },
    ]);
    setColorPresetDraft(emptyColorPresetDraft);
    setColorPresetHexError("");
  }, [parsedColorPresetDraft]);

  const {
    mutateAsync: createStyle,
    isPending,
    data: result,
    error,
  } = createAiEditStyle.useMutation({
    onSuccess: () => {
      toast.success("Video style created successfully");
    },
    onError: () => {
      toast.error("Failed to create video style");
    },
  });

  const onSubmit = useCallback(async () => {
    const payload = {
      id: "",
      videoTemplate: {
        id: "",
        displayName,
        captionsTemplate: captionTemplate
          ? {
              id: captionTemplate,
              displayName: "",
            }
          : undefined,
        captionsColorOverrideConfigs: captionsColorOverrides,
      },
      shotTemplates: [],
      previewUri: "",
      tags: ["premium"],
      ios: {
        inDev: true,
      },
      desktop: {
        inDev: true,
      },
      android: {
        inDev: true,
      },
      thumbnailUri: "",
      supportedAspectRatios: supportedAspectRatios,
      colorPresets: colorPresets.map((color) => ({
        red: (color.red ?? 0) / 255,
        green: (color.green ?? 0) / 255,
        blue: (color.blue ?? 0) / 255,
        alpha: 1,
      })),
    };
    await createStyle({ params: payload });
  }, [
    displayName,
    captionTemplate,
    colorPresets,
    captionsColorOverrides,
    supportedAspectRatios,
    createStyle,
  ]);

  if (!canEdit) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="outline"
          onClick={() => {
            navigate({ to: "/ai-edit-tool" });
          }}
          size="sm"
          className="mr-auto"
        >
          {"< Back"}
        </Button>
        <PermissionBanner requiredPermission="ai-edit-v2-tool:write" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="outline"
        onClick={() => {
          navigate({ to: "/ai-edit-tool" });
        }}
        size="sm"
        className="mr-auto"
      >
        {"< Back"}
      </Button>
      {result && (
        <div className="flex flex-col gap-4">
          <h1 className="text-xl font-semibold font-mono">Success!</h1>
          <h2 className="text-lg font-semibold font-mono">Style ID: {result.style?.id}</h2>
          <pre className="text-xs font-mono bg-muted p-4 rounded overflow-auto">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
      {error && (
        <div className="flex flex-col gap-4">
          <h1 className="text-xl font-semibold font-mono text-destructive">Error!</h1>
          <p className="text-sm font-mono">{error.message}</p>
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </div>
      )}
      {!result && !error && (
        <div className="flex flex-col w-full">
          <div className="mb-8">
            <h1 className="text-xl font-semibold font-mono">New Video Style</h1>
          </div>
          <div className="grid grid-cols-[max-content_max-content] gap-y-2 gap-x-8 w-full items-center">
            <Label className="text-xs font-mono">Display name</Label>
            <Input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="text-xs"
            />
            <Label className="text-xs font-mono">Caption template ID (required)</Label>
            <div className="flex gap-2 items-center">
              <Input
                type="text"
                value={captionTemplate}
                onChange={(e) => setCaptionTemplate(e.target.value)}
                className="text-xs"
              />
              <Popover open={captionTemplatesOpen} onOpenChange={setCaptionTemplatesOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs">
                    Browse
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-80 p-2">
                  <Input
                    value={captionTemplatesSearch}
                    onChange={(e) => setCaptionTemplatesSearch(e.target.value)}
                    placeholder="Search templates"
                    className="text-xs"
                  />
                  <ScrollArea className="h-64 mt-2">
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start text-xs"
                        onClick={() => {
                          setCaptionTemplate("");
                          setCaptionTemplatesOpen(false);
                        }}
                      >
                        Clear selection
                      </Button>
                      {captionsTemplatesLoading && (
                        <div className="text-xs text-muted-foreground px-2 py-1">Loading...</div>
                      )}
                      {!captionsTemplatesLoading &&
                        filteredCaptionTemplates.map((template) => (
                          <Button
                            key={template.id}
                            variant="ghost"
                            size="sm"
                            className="justify-start text-xs"
                            onClick={() => {
                              setCaptionTemplate(template.id);
                              setCaptionTemplatesOpen(false);
                            }}
                          >
                            {template.displayName
                              ? `${template.displayName} (${template.id})`
                              : template.id}
                          </Button>
                        ))}
                      {!captionsTemplatesLoading && filteredCaptionTemplates.length === 0 && (
                        <div className="text-xs text-muted-foreground px-2 py-1">
                          No templates found
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                  {selectedCaptionTemplate && (
                    <div className="text-[10px] text-muted-foreground mt-2">
                      Selected:{" "}
                      {selectedCaptionTemplate.displayName
                        ? `${selectedCaptionTemplate.displayName} (${selectedCaptionTemplate.id})`
                        : selectedCaptionTemplate.id}
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <Label className="text-xs font-mono">Supported aspect ratios</Label>
            <div className="flex gap-4">
              <span className="flex items-center gap-2 text-xs font-mono">
                <Checkbox
                  checked={supportedAspectRatios.includes("9:16")}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSupportedAspectRatios((prev) => [...prev, "9:16"]);
                    } else {
                      setSupportedAspectRatios((prev) => prev.filter((ar) => ar !== "9:16"));
                    }
                  }}
                />
                Vertical (9:16)
              </span>
              <span className="flex items-center gap-2 text-xs font-mono">
                <Checkbox
                  checked={supportedAspectRatios.includes("16:9")}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSupportedAspectRatios((prev) => [...prev, "16:9"]);
                    } else {
                      setSupportedAspectRatios((prev) => prev.filter((ar) => ar !== "16:9"));
                    }
                  }}
                />
                Horizontal (16:9)
              </span>
            </div>
            <Label className="text-xs font-mono">Color presets</Label>
            <div className="flex flex-col gap-3 rounded-md border p-3">
              <div className="flex flex-col gap-2">
                {colorPresets.length === 0 && (
                  <span className="text-[10px] font-mono text-muted-foreground">
                    No color presets yet
                  </span>
                )}
                {colorPresets.map((color, index) => {
                  const red = color.red ?? 0;
                  const green = color.green ?? 0;
                  const blue = color.blue ?? 0;

                  return (
                    <div
                      key={index}
                      className="flex flex-wrap items-center gap-3 rounded border px-2 py-1.5"
                    >
                      <span
                        className="h-5 w-5 rounded border"
                        style={{ backgroundColor: `rgb(${red}, ${green}, ${blue})` }}
                      />
                      <span className="text-xs font-mono">
                        R:{red} G:{green} B:{blue}
                      </span>
                      <span className="text-xs font-mono text-muted-foreground">
                        {rgbColorChannelsToHex({ red, green, blue })}
                      </span>
                      <Button
                        onClick={() =>
                          setColorPresets((previous) => previous.filter((_, i) => i !== index))
                        }
                        size="sm"
                        variant="outline"
                        className="ml-auto h-7"
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="grid gap-3 lg:grid-cols-[max-content_max-content_max-content_minmax(16rem,1fr)] lg:items-end">
                <div className="flex flex-row items-center gap-2">
                  <Label
                    htmlFor="color-preset-red"
                    className="text-xs font-mono uppercase text-muted-foreground"
                  >
                    Red
                  </Label>
                  <Input
                    id="color-preset-red"
                    type="number"
                    min={0}
                    max={255}
                    step={1}
                    className="text-xs"
                    value={colorPresetDraft.red}
                    onChange={(event) => onColorPresetChannelChange("red", event.target.value)}
                  />
                </div>
                <div className="flex flex-row items-center gap-2">
                  <Label
                    htmlFor="color-preset-green"
                    className="text-xs font-mono uppercase text-muted-foreground"
                  >
                    Green
                  </Label>
                  <Input
                    id="color-preset-green"
                    type="number"
                    min={0}
                    max={255}
                    step={1}
                    className="text-xs"
                    value={colorPresetDraft.green}
                    onChange={(event) => onColorPresetChannelChange("green", event.target.value)}
                  />
                </div>
                <div className="flex flex-row items-center gap-2">
                  <Label
                    htmlFor="color-preset-blue"
                    className="text-xs font-mono uppercase text-muted-foreground"
                  >
                    Blue
                  </Label>
                  <Input
                    id="color-preset-blue"
                    type="number"
                    min={0}
                    max={255}
                    step={1}
                    className="text-xs"
                    value={colorPresetDraft.blue}
                    onChange={(event) => onColorPresetChannelChange("blue", event.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label
                    htmlFor="color-preset-hex"
                    className="text-xs font-mono uppercase text-muted-foreground"
                  >
                    Hex
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="color-preset-hex"
                      type="text"
                      value={colorPresetDraft.hex}
                      placeholder="#FFCC00"
                      className="text-xs font-mono"
                      onChange={(event) => {
                        setColorPresetDraft((previous) => ({
                          ...previous,
                          hex: event.target.value,
                        }));
                        setColorPresetHexError("");
                      }}
                      onBlur={() => {
                        if (colorPresetDraft.hex.trim().length > 0) {
                          setColorPresetDraftFromHex(colorPresetDraft.hex, true);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          setColorPresetDraftFromHex(colorPresetDraft.hex, true);
                        }
                      }}
                      onPaste={(event) => {
                        const pasted = event.clipboardData.getData("text");
                        const applied = setColorPresetDraftFromHex(pasted, false);
                        if (applied) {
                          event.preventDefault();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setColorPresetDraftFromHex(colorPresetDraft.hex, true)}
                    >
                      Convert
                    </Button>
                    <span
                      className="h-9 w-9 shrink-0 rounded border"
                      style={
                        colorPresetPreview ? { backgroundColor: colorPresetPreview } : undefined
                      }
                    />
                  </div>
                  {colorPresetHexError.length > 0 && (
                    <span className="text-[10px] font-mono text-destructive">
                      {colorPresetHexError}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={onAddColorPreset}
                  size="sm"
                  className="w-fit"
                  disabled={!parsedColorPresetDraft}
                >
                  Add preset
                </Button>
                <span className="text-[10px] font-mono text-muted-foreground">
                  Enter RGB values or paste hex and convert to RGB.
                </span>
              </div>
            </div>
            <Label className="text-xs font-mono">Captions color overrides</Label>
            <CaptionColorOverridePicker
              overrides={captionsColorOverrides}
              onChange={setCaptionsColorOverrides}
            />
          </div>
          <Button
            onClick={onSubmit}
            disabled={isPending || !captionTemplate.trim()}
            className="mt-4 mr-auto"
          >
            Submit
          </Button>
        </div>
      )}
    </div>
  );
}
